<template>
  <div>
    <CustomTable :options="tableOptions"
                 :table-data="logs"
                 :selected-rows="checkboxController.selectedRows"
                 :bordered-cell="editableCellHandler.cell"
                 class="order-logs-table">
      <!-- Head -->
      <template #headcheckbox>
        <BasicCheckbox id="checkbox-head"
                       :is-checkbox-checked="checkboxController.isCheckboxAllChecked(logs)"
                       :callback="() => selectAllRows(logs)"/>
      </template>

      <!-- Data -->
      <template #checkbox="props">
        <BasicCheckbox :id="`checkbox-${props.rowData.id}`"
                       :is-checkbox-checked="checkboxController.isCheckboxChecked(props.rowData.id)"
                       :callback="() => selectRow(props.rowData)"/>
      </template>
      <template #createdAt="props">
        {{ props.rowData.date }}
      </template>
      <template #employee="props">
        <EmployeeEditableCell :is-edit-supported="!orderDetails.completed"
                              @save="({employeeId}) => editEmployee(employeeId, props.rowData)"
                              @on-active-edit="updateActiveEditableCell"
                              @on-inactive-edit="updateActiveEditableCell"
                              :row-id="props.rowData.id"
                              :text="props.rowData.employee.displayName"
                              :content-id="`name-${props.rowData.id}-${props.rowData.employee.spacelessDisplayName}`"/>
      </template>
      <template #issueKey="props">
        <!-- If the link is not present, the issue key is a simple text. -->
        <DLink v-if="props.rowData.issueLink" :text="props.rowData.issue.key" :link="props.rowData.issueLink"/>
        <span v-else>{{ props.rowData.issue.key }}</span>
      </template>
      <template #exportPreset="props">
        <ExportPreset :is-edit-enabled="!orderDetails.completed"
                      @preset-updated="onExportPresetUpdate"
                      @on-active-edit="updateActiveEditableCell"
                      @on-inactive-edit="updateActiveEditableCell"
                      @specify-reason="(preset) => saveSelectedPreset(preset, props.rowData)"
                      :selected="props.rowData.exportPreset"
                      cell-key="exportPreset"
                      :row-ids="[props.rowData.id]"
                      :content-id="exportPresetBoxId(props.rowData.id)"/>
      </template>
      <template #issueSummary="props">
        {{ props.rowData.issue.summary }}
      </template>
      <template #comment="props">
        <EditableTableCell :is-edit-enabled="!orderDetails.completed"
                           @save="({content}) => editComment(content, props.rowData)"
                           @on-active-edit="updateActiveEditableCell"
                           @on-inactive-edit="updateActiveEditableCell"
                           cell-key="comment"
                           :row-data="props.rowData"
                           :row-id="props.rowData.id"
                           :content-id="`comment-${props.rowData.id}`"/>
      </template>
      <template #hours="props">
        <p class="hours-cell">{{ props.rowData.hours }}</p>
      </template>
      <template #ratio="props">
        <div class="ratio-cell">
          <ConflictTooltip v-if="props.rowData.hasConflict"/>
          <EditableTableCell :is-edit-enabled="!orderDetails.completed"
                             @save="({content}) => saveEnteredRatio(content, props.rowData)"
                             @on-active-edit="updateActiveEditableCell"
                             @on-inactive-edit="updateActiveEditableCell"
                             cell-key="ratio"
                             :row-data="props.rowData"
                             :content-id="`ratio-${props.rowData.id}`"
                             class="editable-cell-ratio"/>
          <ReasonTooltip :log="props.rowData" @specify-reason="() => specifyReason(props.rowData)"/>
        </div>
      </template>
    </CustomTable>

    <!-- Specify reason for ratio change -->
    <SpecifyReason v-if="isSpecifyReasonModalDisplayed"
                   @reason-confirmed="onReasonConfirmed"
                   :type-of-change="typeOfChange"
                   :new-value="newValueOfChange"
                   :logs="[activeRow]"/>
  </div>
</template>

<script>
import {computed, ref} from "@vue/composition-api";
import CustomTable from "@/components/partials/table/CustomTable";
import BasicCheckbox from "@/components/elements/BasicCheckbox";
import CheckboxController from "@/utils/controllers/CheckboxController";
import EditableTableCell from "@/components/partials/table/EditableTableCell";
import EditableRatioCell from "../../table/ratio/EditableRatioCell";
import SearchInputField from "@/components/partials/search/SearchInputField";
import ConflictTooltip from "@/components/elements/ConflictTooltip";
import ExportPreset from "@/components/partials/export/ExportPreset";
import EmployeeEditableCell from "@/components/partials/table/EmployeeEditableCell";
import {EDIT_LOG} from "@/store/log/actions";
import Toast from "@/components/elements/Toast";
import {EditableCellHandler} from "@/utils/helpers/editableCellHelpers";
import ReasonTooltip from "../../reasons/ReasonTooltip";
import {MODAL_NAMES} from "@/models/ModalContent";
import {isChange, REASONS_MODAL_IDS, reasonSetup} from "@/utils/helpers/reasonsHelper";
import {TYPE_OF_CHANGE} from "@/models/reasons/Reason";
import SpecifyReason from "@/components/partials/reasons/SpecifyReason";
import {getModalHelpers} from "../../../../composables/modalHelper";
import initializeOrderLogsTable from "@/composables/order/getOrderLogsTable";
import DLink from "@/components/elements/basicUI/DLink";
import ToastHandler from "@/utils/handlers/toastHandler/ToastHandler";
import {TOAST_CONTENTS} from "@/utils/handlers/toastHandler/ToastContents";

export default {
  name: "OrderLogsTable",
  components: {
    DLink,
    ReasonTooltip,
    SpecifyReason,
    Toast,
    EmployeeEditableCell,
    ExportPreset,
    ConflictTooltip,
    SearchInputField,
    EditableRatioCell,
    EditableTableCell,
    BasicCheckbox,
    CustomTable
  },
  emits: ['selected-rows', 'refresh-table-data'],
  setup(props, {emit, root}) {
    const store = root.$store;

    /** Table **/
    const {tableOptions} = initializeOrderLogsTable();

    /** Emits **/
    const refreshTableData = () => emit('refresh-table-data');
    const updateParentRows = () => emit('selected-rows', checkboxController.value.selectedRows);

    /** Checkboxes **/
    const checkboxController = ref(new CheckboxController());

    function selectRow(rowData) {
      checkboxController.value.selectRow(rowData);
      updateParentRows();
    }

    function selectAllRows() {
      checkboxController.value.selectAllRows(logs.value);
      updateParentRows();
    }

    function clearRows() {
      checkboxController.value.deselectAll();
      updateParentRows();
    }

    /** Edit comment **/
    function editComment(content, row) {
      // Update the comment of the row with the new comment.
      row.comment = content;

      // Edit the log based on the new row data.
      editLog(row).then(response => {
        if (response) {
          refreshTableData();
          resetActiveRow();

          // Display the success toast.
          ToastHandler.addNew(TOAST_CONTENTS.COMMENT_EDITED);
        }
      })
    }

    /** Editable cells **/
    const editableCellHandler = ref(new EditableCellHandler());

    function updateActiveEditableCell(data) {
      editableCellHandler.value.addCell(data);
    }

    /** Edit export preset **/
    const exportPresetBoxId = (id) => `exportPreset${id}`;

    function saveSelectedPreset(preset, row) {
      saveValueAndSpecifyReason(preset.text, row, TYPE_OF_CHANGE.EXPORT_PRESET); // Set the value of the change and the type of change.

      // Display the specify reason modal for the type of change.
      const exportPresetBox = document.getElementById(exportPresetBoxId(row.id));
      displaySpecifyReasonModal(null, () => exportPresetBox.__vue__.selectPreset(), REASONS_MODAL_IDS.ORDER_LOGS);
    }

    function onExportPresetUpdate() {
      refreshTableData(); // Get the updated data from the API.
      closeModal();
    }

    /** Edit employee **/
    const successfulEmployeeEdit = ref(null);

    function editEmployee(employeeId, row) {
      // Set the new employee id.
      row.setEmployeeId(employeeId);

      // Edit the log based on the new row data.
      editLog(row).then(response => {
        if (response) {
          refreshTableData();
          resetActiveRow();
          ToastHandler.addNew(TOAST_CONTENTS.EMPLOYEE_EDITED);
        }
      })
    }

    /** Edit ratio **/
    function editRatio(content, row, reason = "") {
      // Set the new ratio.
      row.ratio = Number(content);

      // Set the reason (if specified).
      row.excludeReason = reason;

      // Edit the log based on the new row data.
      editLog(row).then(response => {
        if (response) {
          refreshTableData();

          // Display the success toast.
          ToastHandler.addNew(TOAST_CONTENTS.RATIO_SINGLE_EDITED(content));
          closeModal();
          resetActiveRow();
        }
      })
    }

    function saveEnteredRatio(ratio, row) {
      saveValueAndSpecifyReason(Number(ratio), row, TYPE_OF_CHANGE.RATIO);
      displaySpecifyReasonModal(null, () => editRatio(ratio, row), REASONS_MODAL_IDS.ORDER_LOGS); // Display the specify reason modal.
    }

    /** Edit log **/
    function editLog(data) {
      // The API call requires all the below properties (even if they have not been edited).
      const payload = {
        "employeeId": data.employeeId,
        "issueId": data.issueId,
        "comment": data.comment,
        "ratio": data.ratio,
        "logId": data.id,
        "excludeReason": data.excludeReason,
      }
      return store.dispatch(EDIT_LOG, payload);
    }

    /** Reset **/
    function resetTable() {
      // Reset the selected rows.
      checkboxController.value.resetSelectedRows();

      // Inform the parent about the rows' reset.
      updateParentRows();
    }

    /** Order logs **/
    const logs = computed(() => store.getters.getSelectedOrder?.logs);

    /** Order Details **/
    const orderDetails = computed(() => store.getters.getSelectedOrder);

    /** Modal **/
    const {activeModal, setModal, closeModal} = getModalHelpers();

    /** Reason for changing a value (ratio, export preset) **/
    const isSpecifyReasonModalDisplayed = computed(() => activeModal.value === MODAL_NAMES.SPECIFY_CHANGE_REASON(REASONS_MODAL_IDS.ORDER_LOGS));
    const {
      activeRow,
      newValueOfChange,
      typeOfChange,
      saveValueAndSpecifyReason,
      displaySpecifyReasonModal,
      resetActiveRow
    } = reasonSetup();

    function onReasonConfirmed(reason) {
      if (isChange(typeOfChange.value, TYPE_OF_CHANGE.RATIO)) {
        editRatio(newValueOfChange.value, activeRow.value, reason);
      } else if (isChange(typeOfChange.value, TYPE_OF_CHANGE.EXPORT_PRESET)) {
        const exportPresetBox = document.getElementById(exportPresetBoxId(activeRow.value.id));
        exportPresetBox.__vue__.selectPreset(reason);
      } else if (isChange(typeOfChange.value, TYPE_OF_CHANGE.DEFAULT)) {
        editLogBasedOnReason(reason);
      }
    }

    function specifyReason(row) {
      saveValueAndSpecifyReason(null, row, TYPE_OF_CHANGE.DEFAULT); // Set the value of the change and the type of change.
      displaySpecifyReasonModal(null, () => editLogBasedOnReason(""), REASONS_MODAL_IDS.ORDER_LOGS); // Display the specify reason modal for the type of change.
    }

    function editLogBasedOnReason(reason) {
      // Set the reason.
      activeRow.value.excludeReason = reason;

      // Edit the log's reason.
      editLog(activeRow.value).then(response => {
        if (response) {
          refreshTableData();
          closeModal();
        }
      })
    }

    return {
      tableOptions,

      /** Checkboxes **/
      checkboxController,
      selectRow,
      selectAllRows,
      clearRows,

      /** Edit comment **/
      editComment,

      /** Editable cells **/
      editableCellHandler,
      updateActiveEditableCell,

      /** Edit export preset **/
      exportPresetBoxId,
      saveSelectedPreset,
      onExportPresetUpdate,

      /** Edit employee **/
      editEmployee,
      successfulEmployeeEdit,

      /** Edit ratio **/
      activeRow,
      saveEnteredRatio,

      /** Reset **/
      resetTable,

      /** Order logs **/
      logs,

      /** Order Details **/
      orderDetails,

      /** Reason for changing a value (ratio, export preset) **/
      isSpecifyReasonModalDisplayed,
      newValueOfChange,
      typeOfChange,
      onReasonConfirmed,
      specifyReason,
    }
  }
}
</script>

<style scoped lang="scss">
@import "../../../../assets/css/base.mixins";

.issue-text {
  color: var(--red-main);
}

.ratio-cell {
  display: flex;
  text-align: right;
  justify-content: center;
  position: relative;

  .editable-ratio {
    display: inline-flex;
  }
}

.hours-cell {
  text-align: center;
}
</style>
